import React, { useEffect, useState } from "react";
import { Icon, IconName } from "@axum/ax-design";
import { useOrdersFailed, useSellerStatsFailed } from "../../../utils/loadErrors";
import { PendingSplit, rangeLabel, usePendingSplit } from "../../../utils/elsewhere";
import { DateRange } from "../../../types/common";
import { ElsewhereButton, KpiRow } from "./styles";

interface Props {
  entity: string;
  ordersTotal: number;
  sellersTotal: number;
  fetchingOrders: boolean;
  reintentosQuantity: number;
  pendienteDescargaQuantity: number;
  hidden: boolean;
  range: DateRange;
  canShow: (entity: string) => boolean;
  onShow: (entity: string, range: DateRange) => void;
}

type Tone = "accent" | "danger" | "warning";

interface Lead {
  label: string;
  icon: IconName;
  tone: Tone;
}

const LEADS: { [entity: string]: Lead } = {
  orders: { label: "Pedidos en el período", icon: "cart", tone: "accent" },
  rejected_orders: { label: "Rechazados en el período", icon: "warning", tone: "danger" },
  budgets: { label: "Presupuestos en el período", icon: "document", tone: "warning" },
  seller_stats: { label: "Vendedores con pedidos", icon: "user", tone: "accent" },
};

const KpiIcon = ({ name, tone }: { name: IconName; tone: Tone }) => (
  <span className={`o360-kpi-icon is-${tone}`}><Icon name={name} /></span>
);

const formatCount = (value: number) => (value || 0).toLocaleString("es-AR");

const NEW_BUDGETS = { logic: "and", filters: [{ field: "erpState", operator: "eq", value: "NUEVO" }] };

interface NoteProps {
  split: PendingSplit | null;
  idle: string;
  onShow?: (range: DateRange) => void;
}

const PendingNote = ({ split, idle, onShow }: NoteProps) => {
  if (!split) return <span className="ax-kpi-card-note">{idle}</span>;
  const elsewhere = `${formatCount(split.elsewhere)} de otras fechas`;
  return (
    <>
      <span className="ax-kpi-card-note">
        {split.inPeriod > 0 ? `${formatCount(split.inPeriod)} en el período` : "ninguno en el período"}
      </span>
      {onShow ? (
        <ElsewhereButton size="sm" variant="soft-primary" data-ax-tooltip={rangeLabel(split.span)} onClick={() => onShow(split.span)}>
          <Icon name="calendar" />{elsewhere}<span className="ax-visually-hidden">: {rangeLabel(split.span)}</span><Icon name="chevron-right" />
        </ElsewhereButton>
      ) : (
        <span className="ax-kpi-card-note">· {elsewhere}</span>
      )}
    </>
  );
};

export default (props: Props) => {
  const isSellers = props.entity === "seller_stats";
  const total = isSellers ? props.sellersTotal : props.ordersTotal;
  const ordersFailed = useOrdersFailed();
  const sellersFailed = useSellerStatsFailed();
  const failed = isSellers ? sellersFailed : ordersFailed;
  const [started, setStarted] = useState(false);
  const rejectedSplit = usePendingSplit("rejected_orders", null, props.reintentosQuantity, props.range);
  const budgetsSplit = usePendingSplit("budgets", NEW_BUDGETS, props.pendienteDescargaQuantity, props.range);
  const lead = LEADS[props.entity] || LEADS.orders;
  const showIn = (entity: string) => (props.canShow(entity) ? (range: DateRange) => props.onShow(entity, range) : undefined);

  useEffect(() => {
    if (props.fetchingOrders) setStarted(true);
  }, [props.fetchingOrders]);

  return (
    <KpiRow
      className={`ax-kpi-row${!started || props.fetchingOrders ? " is-loading" : ""}${props.hidden ? " ax-d-none" : ""}`}
      aria-label="Indicadores"
    >
      <div className="ax-kpi-card ax-kpi-card-lead">
        <span className="ax-kpi-card-label">
          <KpiIcon name={lead.icon} tone={lead.tone} />
          <span>{lead.label}</span>
        </span>
        <span className="ax-kpi-card-figure">
          <span className="ax-kpi-card-value">{failed ? "—" : formatCount(total)}</span>
          <span className="ax-kpi-card-note">{failed ? "no se pudo cargar" : "según los filtros"}</span>
        </span>
      </div>
      <div className="ax-kpi-card">
        <span className="ax-kpi-card-label">
          <KpiIcon name="warning" tone="danger" />
          <span>Envíos rechazados</span>
        </span>
        <span className="ax-kpi-card-figure">
          <span className={`ax-kpi-card-value${props.reintentosQuantity > 0 ? " o360-kpi-alert" : ""}`}>{formatCount(props.reintentosQuantity)}</span>
          <PendingNote split={rejectedSplit} idle="para reintentar o cancelar" onShow={showIn("rejected_orders")} />
        </span>
      </div>
      <div className="ax-kpi-card">
        <span className="ax-kpi-card-label">
          <KpiIcon name="download" tone="warning" />
          <span>Presupuestos por descargar</span>
        </span>
        <span className="ax-kpi-card-figure">
          <span className="ax-kpi-card-value">{formatCount(props.pendienteDescargaQuantity)}</span>
          <PendingNote split={budgetsSplit} idle="nuevos" onShow={showIn("budgets")} />
        </span>
      </div>
    </KpiRow>
  );
};
